<template>
    <div class="border-b border-theme-border w-full py-2">
        <div class="w-full flex justify-between items-center">
            <div class="flex lg:flex-row flex-col">
                <div>
                    <router-link
                        v-if="product.id"
                        :to="`/app/products/detail/${product.id}`"
                        class="btn-a p-0"
                    >
                        <strong> SKU {{ product.sku }}</strong>
                    </router-link>
                    <strong v-else>SKU {{ product.sku }}</strong>
                </div>
            </div>
            <div class="flex items-center">
                <small v-if="product.updatedAt" class="text-center">
                    📆 {{ product.updatedAt | date }}
                </small>
                <app-button icon border circle @click="toggleActive">
                    <ChevronUpIcon v-if="isActive" class="w-5 h-5" />
                    <ChevronDownIcon v-else class="w-5 h-5" />
                </app-button>
                <div v-if="enabled" id="actions" class="flex">
                    <app-button danger icon @click="remove">🗑</app-button>
                </div>
            </div>
        </div>
        <custom-collapse
            ref="container mx-auto"
            :visible="isActive"
            class="w-11/12 mx-auto"
        >
            <div class="flex flex-col">
                <span class="h5">Datos del producto </span>
                <small v-if="!updatesEnabled">
                    Este producto no se actualizará. Adicionalmente, contiene
                    {{ productComponent.referencesAlreadyCreated.length }}
                    {{
                        productComponent.referencesAlreadyCreated.length === 1
                            ? "referencia existente"
                            : "referencias existentes"
                    }}
                    que no se modificarán, y
                    {{ productComponent.referencesToInsert.length }}
                    {{
                        productComponent.referencesToInsert.length === 1
                            ? "nueva referencia que será insertada"
                            : "nuevas referencias que serán insertadas"
                    }}
                    .
                </small>
            </div>
            <div class="flex flex-wrap mb-3">
                <div class="w-full md:w-1/2 mt-6 px-2">
                    <custom-input
                        v-model="productComponent.name"
                        label="Nombre"
                        :disabled="!enabled || !updatesEnabled"
                        @blur="updateProduct"
                    />
                    <div v-if="product.currentName !== undefined">
                        <span class="small">
                            <strong>Nombre actual:</strong>
                            {{ product.currentName | cut(60) }}
                        </span>
                    </div>
                </div>
                <div class="w-full md:w-1/2 mt-6 px-2">
                    <custom-input
                        v-model="productComponent.summary"
                        label="Descripción"
                        :disabled="!enabled || !updatesEnabled"
                        @blur="updateProduct"
                    />
                    <div v-if="product.currentShortDescription !== undefined">
                        <span class="small">
                            <strong> Descripción actual: </strong>
                            {{ product.currentShortDescription | cut(60) }}
                        </span>
                    </div>
                </div>
                <div class="w-full md:w-1/2 mt-6 px-2">
                    <brand-selector
                        v-model="productComponent.brand"
                        :enabled="enabled && updatesEnabled"
                        @change="updateProduct"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-6 px-2">
                    <line-selector
                        v-model="productComponent.subline"
                        :default-filters="{}"
                        :fill="false"
                        :enabled="enabled && updatesEnabled"
                        @change="updateProduct"
                    />
                </div>
            </div>
            <div v-if="productComponent.referencesToInsert.length">
                <span class="h5 my-2">
                    Variantes a insertar
                    <span class="text-theme-primary">
                        {{ product.referencesToInsert.length }}
                    </span>
                </span>
                <reference-simple-manage
                    v-for="reference in productComponent.referencesToInsert"
                    :key="reference.barcode"
                    :reference="reference"
                    :update-enabled="enabled"
                    :stock-enabled="stockEnabled"
                    @remove="removeReferencesToInsert"
                    @update="updateReferencesToInsert"
                />
            </div>
            <div v-if="productComponent.referencesAlreadyCreated.length">
                <span class="h5 my-2">
                    Variantes ya creadas
                    <span class="text-theme-primary">
                        {{ product.referencesAlreadyCreated.length }}
                    </span>
                    {{ !updatesEnabled ? "(No se actualizarán)" : "" }}
                </span>
                <div class="border-top my-3"></div>
                <reference-simple-manage
                    v-for="reference in productComponent.referencesAlreadyCreated"
                    :key="reference.barcode"
                    :reference="reference"
                    :update-enabled="enabled && updatesEnabled"
                    :stock-enabled="stockEnabled"
                    @remove="removeReferenceToUpdate"
                    @update="updateReferenceToUpdate"
                />
            </div>
        </custom-collapse>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import BrandSelector from "@/components/brands/BrandSelector.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import ChevronUpIcon from "@/components/icons/ChevronUpIcon.vue";
import LineSelector from "@/components/lines/LineSelector.vue";
import ReferenceSimpleManage from "@/components/references/ReferenceSimpleManage.vue";
import CustomCollapse from "@/components/utils/CustomCollapse.vue";
import CustomInput from "@/components/utils/CustomInput.vue";

export default {
    name: "ProductWithReferencesManage",
    components: {
        LineSelector,
        BrandSelector,
        ReferenceSimpleManage,
        CustomCollapse,
        CustomInput,
        ChevronDownIcon,
        ChevronUpIcon
    },
    props: {
        product: {
            type: Object,
            required: true,
            default: () => ({
                id: null,
                name: "",
                summary: ""
            })
        },
        enabled: {
            default: () => true,
            type: Boolean,
            required: false
        },
        updatesEnabled: {
            default: () => false,
            type: Boolean,
            required: false
        },
        stockEnabled: {
            default: () => true,
            type: Boolean,
            required: false
        }
    },
    emits: ["update", "remove"],
    data: () => ({
        productComponent: {
            id: null,
            sku: "",
            name: "",
            summary: "",
            currentName: "Tinte duvy class 1.0 negro natural",
            currentShortDescription: "Tinte duvy class 1.0 negro natural",
            referencesToInsert: [],
            referencesAlreadyCreated: [],
            brand: { slug: "marca-blanca", name: "Marca Blanca" }
        },
        isActive: false
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {
        product(value) {
            this.setProduct(value);
        }
    },
    mounted() {
        this.setProduct(this.product);
    },
    methods: {
        removeReferenceToUpdate({ barcode }) {
            if (
                this.productComponent.referencesAlreadyCreated.length === 1 &&
                !this.productComponent.referencesToInsert.length
            ) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "No puedes dejar productos sin referencias",
                    icon: "info"
                });
                return false;
            }
            const index =
                this.productComponent.referencesAlreadyCreated.findIndex(
                    (item) => item.barcode === barcode
                );
            let referencesAlreadyCreated = [
                ...this.productComponent.referencesAlreadyCreated
            ];
            referencesAlreadyCreated.splice(index, 1);
            this.productComponent.referencesAlreadyCreated = [
                ...referencesAlreadyCreated
            ];
            this.updateProduct();
        },
        updateReferenceToUpdate(payload) {
            const index =
                this.productComponent.referencesAlreadyCreated.findIndex(
                    (item) => item.barcode === payload.barcode
                );
            let referencesAlreadyCreated = [
                ...this.productComponent.referencesAlreadyCreated
            ];
            referencesAlreadyCreated[index] = {
                ...payload
            };
            this.productComponent.referencesAlreadyCreated = [
                ...referencesAlreadyCreated
            ];
            this.updateProduct();
        },
        removeReferencesToInsert({ barcode }) {
            if (
                this.productComponent.referencesToInsert.length === 1 &&
                !this.productComponent.referencesAlreadyCreated.length
            ) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "No puedes dejar productos sin referencias",
                    icon: "info"
                });
                return false;
            }
            const index = this.productComponent.referencesToInsert.findIndex(
                (item) => item.barcode === barcode
            );
            let referencesToInsert = [
                ...this.productComponent.referencesToInsert
            ];
            referencesToInsert.splice(index, 1);
            this.productComponent.referencesToInsert = [...referencesToInsert];
            this.updateProduct();
        },
        updateReferencesToInsert(payload) {
            const index = this.productComponent.referencesToInsert.findIndex(
                (item) => item.barcode === payload.barcode
            );
            let referencesToInsert = [
                ...this.productComponent.referencesToInsert
            ];
            referencesToInsert[index] = {
                ...payload
            };
            this.productComponent.referencesToInsert = [...referencesToInsert];
            this.updateProduct();
        },
        remove() {
            this.$emit("remove", { sku: this.product.sku });
        },
        updateProduct() {
            this.$emit("update", this.productComponent);
        },
        setProduct(product) {
            this.productComponent = {
                ...product
            };
        },
        toggleActive() {
            this.isActive = !this.isActive;
        }
    }
};
</script>
